import { Component, OnDestroy } from '@angular/core';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import printJS from 'print-js';
import { Observable, Subscription } from 'rxjs';

import { BillParams } from '../../../../../../../../data/dietitian/domain/bill/bill-params';
import { Credit } from '../../../../../../../../data/dietitian/domain/credit/credit';
import { Dietitian } from '../../../../../../../../data/dietitian/domain/dietitian';
import { ConfirmationModalComponent } from '../../../../../../../components/confirmation-modal/confirmation-modal.component';
import { EditCreditModalComponent } from '../../../../../../../components/edit-credit-modal/edit-credit-modal.component';
import { PeriodBarResult } from '../../../../../../../components/period-bar/period-bar.component';
import { AccountInformationsViewModel } from '../../../../../account/routes/account-informations/account-informations.facade';
import { BillingFacade, CreditFilter } from '../../../../billing.facade';
import { BillingService } from '../../../../billing.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-credits',
  templateUrl: './credits.component.html',
  styleUrls: ['./credits.component.scss'],
})
export class CreditsComponent implements OnDestroy {
  public dietitian?: Dietitian;
  public isLoading = false;
  public isAppendLoading = false;
  public isOnError = false;
  public creditList: Credit[] | undefined;
  public creditFilter: CreditFilter = { period: undefined, status: undefined };
  public totalExcl = '-';
  public totalIncl = '-';
  public count = 0;
  public billParams: BillParams;
  private currentPage = 0;
  private accountStatus$: Observable<AccountInformationsViewModel>;
  private accountStatusSubscription: Subscription;
  private creditEventSubscription: Subscription;
  private hasCredit = false;

  constructor(
    public facade: BillingFacade,
    public billingService: BillingService,
    private modalService: BsModalService,
    private toastr: ToastrService,
  ) {
    this.isLoading = false;
    this.accountStatus$ = this.facade.accountStatus$;
    this.accountStatusSubscription = this.accountStatus$.subscribe(
      async (state) => {
        this.dietitian = state.dietitian;
        this.isLoading = true;
        this.billParams = await this.facade.loadBillParams(
          this.dietitian.dietitianId.id.toString(),
        );
        this.isLoading = false;
        this.loadPage(0);
      },
    );
    this.creditEventSubscription = this.facade.creditEventProvider$.subscribe(
      () => {
        this.loadPage(0);
      },
    );
  }

  ngOnDestroy(): void {
    this.accountStatusSubscription?.unsubscribe();
    this.creditEventSubscription?.unsubscribe();
  }

  onScroll() {
    this.loadPage(this.currentPage + 1);
  }

  loadPage(page: number) {
    if (!this.dietitian) {
      return;
    }
    if (page === 0) {
      this.isLoading = true;
      this.creditList = [];
    } else {
      this.isAppendLoading = true;
    }
    this.facade
      .findAllCreditArchived(
        this.dietitian.id.toString(),
        this.creditFilter,
        this.creditList && this.creditList.length > 0
          ? this.creditList[this.creditList.length - 1].id.toString()
          : undefined,
      )
      .then((credits) => {
        credits.forEach((b) => this.creditList?.push(b));
        this.currentPage = page;
        this.isLoading = false;
        this.isAppendLoading = false;
        this.isOnError = false;
      })
      .catch(() => {
        this.isOnError = true;
      });

    this.facade.calcTotCredit(true, this.creditFilter).then((res) => {
      this.count = res.count;
      this.totalExcl = this.facade.format2Decimals(res.amountExcludingTax);
      this.totalIncl = this.facade.format2Decimals(res.amountIncludingTax);
    });
  }

  async view(credit: Credit) {
    console.log('👀 view credit', { id: credit.id.toString() });
    if (!this.hasCredit) {
      this.hasCredit = true;
      const mode: 'create' | 'edit' | 'view' | undefined = 'view';
      const initialState = {
        class: 'modal-lg',
        initialState: {
          target: credit.patientId
            ? await this.facade.loadPatient(credit.patientId.id.toString())
            : credit.thirdParty,
          dietitian: this.dietitian,
          credit,
          mode,
        },
      };
      this.modalService.show(EditCreditModalComponent, initialState);
      this.hasCredit = false;
    }
  }

  restore(credit: Credit) {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Restaurer cet avoir ?',
        subtitle:
          "L'avoir sera déplacée dans l'onglet \"Avoirs\". L'action est réversible.",
      },
    };
    const modal = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (modal.content instanceof ConfirmationModalComponent) {
      const subscription = modal.content.validate.subscribe(() => {
        this.facade.restoreCredit(credit).then(() => {
          this.loadPage(0);
          this.billingService.countRefresh.next(credit);
        });
        subscription?.unsubscribe();
      });
    }
  }

  async download(credit: Credit) {
    if (this.dietitian) {
      const loadedCredit = await this.facade.loadCredit(
        this.dietitian.id.toString(),
        credit.id.toString(),
      );
      const blob: Blob | undefined = await new Promise((resolve) => {
        if (!loadedCredit.url) {
          return resolve(undefined);
        }

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function () {
          resolve(xhr.response);
        };
        xhr.onerror = function () {
          resolve(undefined);
        };
        xhr.open('GET', loadedCredit.url);
        xhr.send();
      });

      if (!blob || blob.size <= 0) {
        this.toastr.error(
          "L'avoir PDF n'est pas encore généré. essayez à nouveau dans quelques instants.",
        );
        return;
      }

      let downloadButton = document.createElement('a');
      downloadButton.href = window.URL.createObjectURL(blob);
      downloadButton.setAttribute(
        'download',
        'avoir_' +
          loadedCredit.referencePrefix +
          '-' +
          loadedCredit.reference?.toString().padStart(4, '0'),
      );
      downloadButton = document.body.appendChild(downloadButton);
      downloadButton.click();
      document.body.removeChild(downloadButton);
    } else {
      this.toastr.error('Erreur, veuillez essayer à nouveau ultérieurement.');
    }
  }

  async print(credit: Credit) {
    if (this.dietitian) {
      const loadedCredit = await this.facade.loadCredit(
        this.dietitian.id.toString(),
        credit.id.toString(),
      );
      const blob: Blob | undefined = await new Promise((resolve) => {
        if (!loadedCredit.url) {
          return resolve(undefined);
        }

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function () {
          resolve(xhr.response);
        };
        xhr.onerror = function () {
          resolve(undefined);
        };
        xhr.open('GET', loadedCredit.url);
        xhr.send();
      });

      if (!blob || blob.size <= 0) {
        this.toastr.error(
          "L'avoir' PDF n'est pas encore généré. essayez à nouveau dans quelques instants.",
        );
        return;
      }
      const url: string = window.URL.createObjectURL(blob);
      printJS(url, 'pdf');
    } else {
      this.toastr.error('Erreur, veuillez essayer à nouveau ultérieurement.');
    }
  }

  onPeriodFilter(periodBarResult: PeriodBarResult) {
    this.creditFilter.period = periodBarResult;
    this.loadPage(0);
  }

  countCredit() {
    if (!this.isLoading) {
      return this.count + ' avoir' + (this.count && this.count > 1 ? 's' : '');
    }
    return '';
  }

  totalExclCredit() {
    if (!this.isLoading) {
      return this.totalExcl;
    }
    return '';
  }

  totalInclCredit() {
    if (!this.isLoading) {
      return this.totalIncl;
    }
    return '';
  }

  async export() {
    if (this.dietitian) {
      const bills = await this.facade.findAllCreditExported(
        this.dietitian.id.toString(),
        this.creditFilter,
        true,
      );
      const csv = await this.billingService.exportCredit(
        bills,
        this.dietitian.id.toString(),
      );
      const a = document.createElement('a');
      a.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
      const d = new Date();
      a.setAttribute('download', 'avoir_archive_' + d.toISOString() + '.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }
}
